//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { useContentSourceAddProductsToCartMutation } from '@campaigns/components/content-source/ContentSourceQueries';
import { useBulkActionsSelectedItems } from '@campaigns/components/content-source/available-reviews/selectors/useContentSourceSelectedItems';
import { ContentSourceTrackingIds } from '@campaigns/constants/content-source/ContentSourceTrackingIds';
import { PrimaryButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { SectionText } from '@components/atoms/text/Text';
import makeAngularReactController from '@react-bridge/angular-react-widget';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';

export const BulkSelectProducts = () => {
  const dispatch = useDispatch();

  const selectedItems = useBulkActionsSelectedItems();
  const { setSelectedItemsForContentSource } = getBulkActionsActions();

  const { isLoading: isAddingProduct, mutate: addToCart } =
    useContentSourceAddProductsToCartMutation();

  const clearSelectedProducts = () => {
    dispatch(setSelectedItemsForContentSource([]));
  };

  if (selectedItems.length === 0) {
    return null;
  }

  return (
    <BulkActionsBar>
      <SelectedItems>
        <CloseIcon aria-label='Close' onClick={clearSelectedProducts}>
          <Icon name='close' />
        </CloseIcon>

        <StyledSectionText>{selectedItems.length} selected</StyledSectionText>
      </SelectedItems>
      <AddToCartButton
        isDisabled={isAddingProduct}
        onClick={() => {
          addToCart({ casProductIds: selectedItems });
          clearSelectedProducts();
        }}
        trackingId={
          ContentSourceTrackingIds.AvailableReviewsProductCardAddToCartBulkButton
        }
      >
        Add to Cart
      </AddToCartButton>
    </BulkActionsBar>
  );
};

const BulkActionsBar = styled.div(
  ({ theme }) => `
    display: flex;
    position: absolute;
    top: 0px;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: ${theme.spacings[3]} ${theme.spacings[5]};
    z-index: ${theme.zindex.navbar}; // setting z-index so it appears above top navigation bar
    background-color: ${theme.colors.aliases.primary};
    color: ${theme.colors.white};
  `,
);

const SelectedItems = styled.div(
  ({ theme }) => `
    display: flex;
    align-items: baseline;
    gap: ${theme.spacings[5]};
  `,
);

const StyledSectionText = styled(SectionText)(
  ({ theme }) => `
    color: ${theme.colors.white};
  `,
);

const CloseIcon = styled.span`
  cursor: pointer;
`;

const AddToCartButton = styled(PrimaryButton)(
  ({ theme }) => `
    border: ${theme.borderWidths[2]} solid ${theme.colors.white}; 
  `,
);

angular.module('curalate').component('bulkSelectContentSourceProducts', {
  controller: makeAngularReactController(<BulkSelectProducts />),
});
